import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  Input,
  Button,
  Modal,
  Form,
  message,
  Space,
  Spin,
  Select,
  Menu,
  Dropdown,
  InputNumber,
  Checkbox,
} from "antd";
import { Material } from "../../types/material.type";
import { UnitOfMeasurement } from "../../types/unitOfMeasurement.type";
import { DownOutlined } from "@ant-design/icons";
import { api } from "../../api/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/auth.context";
import { Currency } from "../../types/currency.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { useExchangeRate } from "../../contexts/exchangeRate.context";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function Materials() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [unitFilter, setUnitFilter] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [belowLimit, setBelowLimit] = useState<boolean>(false);
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(
    null
  );
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [form] = Form.useForm();
  const { exchangeRate } = useExchangeRate();

  useEffect(() => {
    fetchMaterials();
  }, [page, searchTerm, unitFilter, sortBy, sortOrder, pageSize, belowLimit]);

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/material`, {
        params: {
          page,
          limit: pageSize,
          name: searchTerm,
          unitOfMeasurement: unitFilter,
          sortBy,
          order: sortOrder,
          belowLimit: belowLimit ? true : undefined,
        },
      });
      setMaterials(response.data.materials);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching materials:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleAddMaterial = () => {
    form.resetFields();
    setSelectedMaterial(null);
    setIsModalOpen(true);
  };

  const handleEditMaterial = (material: Material) => {
    const data = {
      ...material,
      price: {
        amount: material.price[material.price.currency],
        exchangeRate: material.price.exchangeRate,
        currency: material.price.currency,
      },
    };
    form.setFieldsValue(data);
    setSelectedMaterial(material);
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (values: any) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
      };
      if (selectedMaterial) {
        await api.put(`/material/${selectedMaterial._id}`, data);
        message.success("Материал обновлен.");
      } else {
        await api.post(`/material`, data);
        message.success("Материал добавлен.");
      }
      fetchMaterials();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving material:", error);
      message.error(error.response.data.error);
    }
  };

  const handleExport = () => {
    downloadFile("/export/materials", "materials_data.xlsx");
  };

  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
    },
    {
      title: "Ед. измерения",
      dataIndex: "unitOfMeasurement",
      key: "unitOfMeasurement",
    },
    { title: "Всего", dataIndex: "total", key: "total" },
    {
      title: "Остаток",
      dataIndex: "residue",
      key: "residue",
      render: (_: any, record: Material) => {
        const { residue, lowerLimit, upperLimit } = record;

        let backgroundColor = "";
        if (lowerLimit !== undefined && upperLimit !== undefined) {
          if (residue < lowerLimit) {
            backgroundColor = "red";
          } else if (residue <= upperLimit) {
            backgroundColor = "yellow";
          } else if (residue > upperLimit) {
            backgroundColor = "red";
          }
        }

        return {
          props: {
            style: { background: backgroundColor },
          },
          children: <div>{roundToThreeDecimals(residue)}</div>,
        };
      },
    },
    {
      title: "Цена USD",
      key: "priceUSD",
      render: (_: any, record: Material) => {
        return record.price && record.price.USD !== undefined
          ? `${record.price.USD}`
          : "Н/Д";
      },
    },
    {
      title: "Цена UZS",
      key: "priceUZS",
      render: (_: any, record: Material) => {
        return record.price && record.price.UZS !== undefined
          ? `${record.price.UZS}`
          : "Н/Д";
      },
    },
    {
      title: "Продано (кол-во)",
      key: "totalSold",
      render: (_: any, material: Material) =>
        roundToThreeDecimals(material.totalSold),
    },
    {
      title: "Куплено (кол-во)",
      key: "totalPurchased",
      render: (_: any, material: Material) =>
        roundToThreeDecimals(material.totalPurchased),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, material: Material) => {
        const menu = (
          <Menu>
            {authState.user?.role === "admin" && (
              <>
                <Menu.Item onClick={() => handleEditMaterial(material)}>
                  Изменить
                </Menu.Item>
                <Menu.Item
                  onClick={() =>
                    navigate(
                      `/${material._id}/material/${material.name}/batches`
                    )
                  }
                >
                  Смотреть партии
                </Menu.Item>
                <Menu.Item
                  onClick={() =>
                    navigate(
                      `/${material._id}/kind/all/${material.name}/orders`
                    )
                  }
                >
                  Смотреть заказы
                </Menu.Item>
              </>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title>Материалы</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <Select
          placeholder="Фильтр по ед. измерения"
          onChange={(value) => setUnitFilter(value)}
          allowClear
          style={{ width: 200 }}
          showSearch
          filterOption={(input, option) => {
            const optionText = option?.label || option?.children;
            return String(optionText).toLowerCase().includes(input.toLowerCase());
          }}
          optionFilterProp="children"
        >
          {Object.values(UnitOfMeasurement).map((unit) => (
            <Option key={unit} value={unit}>
              {unit}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Сортировать по"
          defaultValue="name"
          onChange={(value) => setSortBy(value)}
          style={{ width: 150 }}
        >
          <Option value="name">Название</Option>
          <Option value="residue">Остаток</Option>
          <Option value="createdAt">Дата</Option>
          <Option value="totalSold">Кол-во продаж</Option>
          <Option value="totalPurchased">Кол-во покупок</Option>
        </Select>
        <Select
          defaultValue="asc"
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 120 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>
        <Checkbox
          checked={belowLimit}
          onChange={(e) => setBelowLimit(e.target.checked)}
        >
          Ниже лимита
        </Checkbox>
        {authState.user?.role === "admin" && (
          <Button type="primary" onClick={handleAddMaterial}>
            Добавить
          </Button>
        )}
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
      </Space>

      {loading ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={materials}
          rowKey={(record) => record._id}
          pagination={{
            current: page,
            total: total,
            pageSize: pageSize,
            onChange: (page, size) => {
              setPage(page);
              if (size !== pageSize) setPageSize(size!);
            },
          }}
          scroll={{ x: "max-content" }}
        />
      )}

      <Modal
        title={selectedMaterial ? "Изменить материал" : "Добавить материал"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleFormSubmit} layout="vertical">
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Введите название" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="unitOfMeasurement"
            label="Ед. измерения"
            rules={[{ required: true, message: "Выберите единицу измерения" }]}
          >
            <Select 
              placeholder="Выберите единицу измерения"
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.label || option?.children;
                return String(optionText).toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="children"
            >
              {Object.values(UnitOfMeasurement).map((unit) => (
                <Option key={unit} value={unit}>
                  {unit}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="lowerLimit"
            label="Нижный лимит"
            rules={[{ required: true, message: "Введите лимит" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="upperLimit"
            label="Верхний лимит"
            rules={[{ required: true, message: "Введите лимит" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name={["price", "currency"]}
            label="Валюта"
            rules={[{ required: true, message: "Выберите валюту" }]}
          >
            <Select 
              placeholder="Выберите валюту"
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.label || option?.children;
                return String(optionText).toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="children"
            >
              {Object.values(Currency).map((currency) => (
                <Option key={currency} value={currency}>
                  {currency}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name={["price", "amount"]}
            label="Цена"
            rules={[{ required: true, message: "Введите цену" }]}
          >
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            name={["price", "exchangeRate"]}
            label="Курс"
            rules={[{ required: true, message: "Введите курс" }]}
            hidden={true}
          >
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
